import Button from "@intility/bifrost-react/Button";
import Grid from "@intility/bifrost-react/Grid";
import Input from "@intility/bifrost-react/Input";
import Message from "@intility/bifrost-react/Message";
import Section from "@intility/bifrost-react/Section";
import Select from "@intility/bifrost-react-select";
import { useMemo, useState } from "react";
import { getBlueprints, readTuples, writeTuples } from "~/api/client";
import type {
  BlueprintNode,
  BlueprintResponse,
  TupleKey,
} from "~/api/fga-types";

type PathSegment = {
  typeId: string;
  typeSlug: string;
  nameRegex: string | null;
  value: string;
};

const PLATFORM_RELATIONS = ["company", "admin", "writer", "drafter", "reader"];
const RESOURCE_RELATIONS = ["parent", "admin", "writer", "drafter", "reader"];

// The relations that exist at a path depth: 0 is the platform itself, anything
// deeper is a resource. The wizard's relation state must always be a member of
// the set for the current depth, or the written tuple fails schema validation.
const relationsForDepth = (depth: number): string[] =>
  depth > 0 ? RESOURCE_RELATIONS : PLATFORM_RELATIONS;

interface Props {
  onSuccess: (message: string) => void;
  onCancel: () => void;
}

// Walk the blueprint tree to the children of the last picked node.
function frontierAt(
  blueprint: BlueprintResponse | null,
  platformSlug: string | null,
  path: PathSegment[],
): BlueprintNode[] {
  if (!blueprint || !platformSlug) return [];
  const platform = blueprint.platforms.find((p) => p.platform === platformSlug);
  if (!platform) return [];
  let nodes = platform.tree;
  for (const seg of path) {
    const found = nodes.find((n) => n.type.id === seg.typeId);
    if (!found) return [];
    nodes = found.children;
  }
  return nodes;
}

export default function BlueprintTupleWizard({ onSuccess, onCancel }: Props) {
  const [companyId, setCompanyId] = useState("");
  const [blueprint, setBlueprint] = useState<BlueprintResponse | null>(null);
  const [loadingBp, setLoadingBp] = useState(false);

  const [platformSlug, setPlatformSlug] = useState<string | null>(null);
  const [path, setPath] = useState<PathSegment[]>([]);

  const [nextTypeId, setNextTypeId] = useState<string | null>(null);
  const [nextValue, setNextValue] = useState("");

  // "reader" is a valid platform relation and leaves the user input editable;
  // the structural relations (parent / company) auto-fill it instead.
  const [relation, setRelation] = useState<string>("reader");
  const [userInput, setUserInput] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const platformShort = platformSlug
    ? platformSlug.split("/").slice(-1)[0]
    : "";

  const frontier = useMemo(
    () => frontierAt(blueprint, platformSlug, path),
    [blueprint, platformSlug, path],
  );

  const objectId = useMemo(() => {
    if (!companyId || !platformShort) return "";
    if (path.length === 0) return `platform:${companyId}/${platformShort}`;
    return `resource:${companyId}/${platformShort}/${path.map((p) => p.value).join("/")}`;
  }, [companyId, platformShort, path]);

  const validRelations = relationsForDepth(path.length);

  // Auto-filled user for structural relations (parent / company).
  const autoFilledUser = useMemo(() => {
    if (relation === "parent" && path.length > 0) {
      if (path.length === 1) return `platform:${companyId}/${platformShort}`;
      return `resource:${companyId}/${platformShort}/${path
        .slice(0, -1)
        .map((p) => p.value)
        .join("/")}`;
    }
    if (relation === "company" && path.length === 0 && companyId) {
      return `company:${companyId}`;
    }
    return null;
  }, [relation, path, companyId, platformShort]);

  const effectiveUser = autoFilledUser ?? userInput;

  const nextRegexError = useMemo(() => {
    if (!nextTypeId) return null;
    const node = frontier.find((n) => n.type.id === nextTypeId);
    if (!node?.type.nameRegex || !nextValue) return null;
    try {
      return new RegExp(node.type.nameRegex).test(nextValue)
        ? null
        : `does not match ${node.type.nameRegex}`;
    } catch {
      return "type's nameRegex is invalid";
    }
  }, [nextTypeId, nextValue, frontier]);

  const handleLoadBlueprint = async () => {
    const id = companyId.trim();
    if (!id) return;
    setLoadingBp(true);
    setError(null);
    try {
      // Every company's platform instances share the one blueprint of their
      // Platform, so the company only shapes the object ids below.
      const bp = await getBlueprints();
      setBlueprint(bp);
      setPlatformSlug(null);
      setPath([]);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBlueprint(null);
    } finally {
      setLoadingBp(false);
    }
  };

  const handleAddSegment = () => {
    if (!nextTypeId || !nextValue.trim() || nextRegexError) return;
    const node = frontier.find((n) => n.type.id === nextTypeId);
    if (!node) return;
    setPath([
      ...path,
      {
        typeId: node.type.id,
        typeSlug: node.type.slug,
        nameRegex: node.type.nameRegex ?? null,
        value: nextValue.trim(),
      },
    ]);
    setNextTypeId(null);
    setNextValue("");
    // Reset the relation if it no longer fits the new depth's relation set
    // (e.g. "company" is platform-only).
    const newRelations = relationsForDepth(path.length + 1);
    if (!newRelations.includes(relation)) {
      setRelation(newRelations[0] ?? "admin");
    }
  };

  const handlePopSegment = () => {
    setPath(path.slice(0, -1));
    // Reset relation if it no longer fits the new depth's relation set.
    const newRelations = relationsForDepth(path.length - 1);
    if (!newRelations.includes(relation)) {
      setRelation(newRelations[0] ?? "admin");
    }
  };

  // Read existing parent (or 'company') tuple for a given object.
  const readStructuralTuple = async (
    obj: string,
    rel: "parent" | "company",
  ): Promise<boolean> => {
    const res = await readTuples({
      tupleKey: { object: obj, relation: rel },
      pageSize: 1,
    });
    return res.tuples.length > 0;
  };

  const handleSubmit = async () => {
    if (!objectId || !relation || !effectiveUser) return;
    setSubmitting(true);
    setError(null);
    try {
      const writes: TupleKey[] = [];

      // Ensure platform → company link unless this is the tuple being created.
      const platformObj = `platform:${companyId}/${platformShort}`;
      const isCreatingPlatformLink =
        path.length === 0 && relation === "company";
      if (!isCreatingPlatformLink) {
        const exists = await readStructuralTuple(platformObj, "company");
        if (!exists) {
          writes.push({
            user: `company:${companyId}`,
            relation: "company",
            object: platformObj,
          });
        }
      }

      // For each resource level, ensure the parent tuple exists (unless the
      // tuple being created IS that parent tuple).
      for (let i = 1; i <= path.length; i++) {
        const childResource = `resource:${companyId}/${platformShort}/${path
          .slice(0, i)
          .map((p) => p.value)
          .join("/")}`;
        const isUserTuple = i === path.length && relation === "parent";
        if (isUserTuple) continue;

        const exists = await readStructuralTuple(childResource, "parent");
        if (!exists) {
          const parent =
            i === 1
              ? platformObj
              : `resource:${companyId}/${platformShort}/${path
                  .slice(0, i - 1)
                  .map((p) => p.value)
                  .join("/")}`;
          writes.push({
            user: parent,
            relation: "parent",
            object: childResource,
          });
        }
      }

      writes.push({ user: effectiveUser, relation, object: objectId });

      await writeTuples({ writes });
      onSuccess(
        writes.length === 1
          ? "1 tuple written"
          : `${writes.length} tuples written (${writes.length - 1} parent link${writes.length - 1 === 1 ? "" : "s"} auto-filled)`,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  };

  const platformOptions =
    blueprint?.platforms.map((p) => ({
      value: p.platform,
      label: p.platform,
    })) ?? [];
  const platformValue = platformSlug
    ? { value: platformSlug, label: platformSlug }
    : null;

  const typeOptions = frontier.map((n) => ({
    value: n.type.id,
    label: `${n.type.displayName} (${n.type.slug})${n.type.nameRegex ? ` — /${n.type.nameRegex}/` : ""}`,
  }));
  const typeValue = nextTypeId
    ? (typeOptions.find((o) => o.value === nextTypeId) ?? null)
    : null;

  const relationOptions = validRelations.map((r) => ({ value: r, label: r }));
  const relationValue = { value: relation, label: relation };

  const userInputDisabled = autoFilledUser !== null;
  const canSubmit =
    !!objectId && !!relation && !!effectiveUser.trim() && !submitting;

  return (
    <Section>
      <Section.Header>Build Tuple from Blueprint</Section.Header>
      <Section.Content>
        <Grid cols={1} small={2} gap={12}>
          <Input
            label="Company GUID"
            placeholder="020a40ac-5674-4027-adc5-0134335ead1c"
            value={companyId}
            onChange={(e) => setCompanyId(e.target.value)}
          />
          <div style={{ display: "flex", alignItems: "flex-end" }}>
            <Button
              onClick={handleLoadBlueprint}
              disabled={loadingBp || !companyId.trim()}
            >
              {loadingBp ? "Loading…" : "Load blueprint"}
            </Button>
          </div>
        </Grid>
      </Section.Content>

      {blueprint && blueprint.platforms.length === 0 && (
        <Section.Content borderTop>
          <Message state="warning" header="No blueprints">
            You cannot see a platform blueprint. Register a platform before you
            create tuples for it.
          </Message>
        </Section.Content>
      )}

      {blueprint && blueprint.platforms.length > 0 && (
        <Section.Content borderTop>
          <Grid cols={1} small={2} gap={12}>
            <Select
              label="Platform"
              placeholder="Select a platform…"
              options={platformOptions}
              value={platformValue}
              onChange={(opt) => {
                setPlatformSlug(
                  (opt as { value: string } | null)?.value ?? null,
                );
                setPath([]);
                setNextTypeId(null);
                setNextValue("");
              }}
              isClearable
            />
          </Grid>
        </Section.Content>
      )}

      {platformSlug && (
        <Section.Content borderTop>
          <p className="bf-p" style={{ marginBottom: 8 }}>
            <strong>Path</strong>
          </p>
          <ul style={{ fontFamily: "monospace", fontSize: 13, marginTop: 0 }}>
            <li>
              <code>
                platform:{companyId}/{platformShort}
              </code>
            </li>
            {path.map((seg, idx) => (
              <li key={seg.typeId} style={{ marginLeft: 16 }}>
                ↳ <code>{seg.value}</code>{" "}
                <span style={{ opacity: 0.7 }}>({seg.typeSlug})</span>
                {idx === path.length - 1 && (
                  <Button
                    small
                    variant="flat"
                    onClick={handlePopSegment}
                    style={{ marginLeft: 8 }}
                  >
                    Remove
                  </Button>
                )}
              </li>
            ))}
          </ul>
          {frontier.length === 0 ? (
            <p className="bf-p" style={{ opacity: 0.7 }}>
              No deeper types in the blueprint. Finalize the tuple below.
            </p>
          ) : (
            <Grid cols={1} small={3} gap={12}>
              <Select
                label="Next segment type"
                placeholder="Select a type…"
                options={typeOptions}
                value={typeValue}
                onChange={(opt) =>
                  setNextTypeId(
                    (opt as { value: string } | null)?.value ?? null,
                  )
                }
                isClearable
              />
              <Input
                label="Segment value"
                placeholder={
                  nextTypeId
                    ? (frontier.find((n) => n.type.id === nextTypeId)?.type
                        .nameRegex ?? "value")
                    : "value"
                }
                value={nextValue}
                onChange={(e) => setNextValue(e.target.value)}
                state={nextRegexError ? "alert" : undefined}
                feedback={nextRegexError ?? undefined}
              />
              <div style={{ display: "flex", alignItems: "flex-end" }}>
                <Button
                  small
                  onClick={handleAddSegment}
                  disabled={
                    !nextTypeId || !nextValue.trim() || !!nextRegexError
                  }
                >
                  Add segment
                </Button>
              </div>
            </Grid>
          )}
        </Section.Content>
      )}

      {platformSlug && (
        <Section.Content borderTop>
          <p className="bf-p" style={{ marginBottom: 8 }}>
            <strong>Tuple preview</strong>
          </p>
          <pre
            style={{
              fontFamily: "monospace",
              fontSize: 13,
              padding: 12,
              backgroundColor: "var(--bf-base-bg)",
              border: "1px solid var(--bf-border-color)",
              borderRadius: 4,
              overflow: "auto",
            }}
          >
            {`(${effectiveUser || "<user>"}, ${relation}, ${objectId})`}
          </pre>
          <Grid cols={1} small={2} gap={12}>
            <Select
              label="Relation"
              options={relationOptions}
              value={relationValue}
              onChange={(opt) => {
                const v = (opt as { value: string } | null)?.value;
                if (v) setRelation(v);
              }}
            />
            <Input
              label={userInputDisabled ? "User (auto-filled)" : "User"}
              placeholder="user:<sub> or group:<slug>#member"
              value={effectiveUser}
              onChange={(e) => setUserInput(e.target.value)}
              disabled={userInputDisabled}
            />
          </Grid>
        </Section.Content>
      )}

      {error && (
        <Section.Content borderTop>
          <Message state="alert" header="Error">
            {error}
          </Message>
        </Section.Content>
      )}

      <Section.Content borderTop>
        <div style={{ display: "flex", gap: 8 }}>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {submitting ? "Writing…" : "Write tuple"}
          </Button>
          <Button variant="flat" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </Section.Content>
    </Section>
  );
}
